import React, { useCallback, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getSpotImages, getThumbUrl } from '../../services/firebaseService';

interface Props {
  /** Plass-id — brukes til å hente resten av bildene FØRST når brukeren blar. */
  spotId?: string;
  /** Allerede kjent cover-bilde (RÅ URL — karusellen thumb-er selv og faller
   *  tilbake på rå URL hvis thumben 404-er). Vises umiddelbart. */
  coverUrl: string;
  /** Ev. kjente ekstra-URL-er (f.eks. anmelderens egne bilder) uten henting. */
  extraUrls?: string[];
  /** Kalles når ALLE kjente bilder er døde (både thumb og rå URL 404-er), slik
   *  at kortet kan falle tilbake på satellitt-visning i stedet for et brutt bilde. */
  onAllBroken?: () => void;
}

/**
 * Bilde-karusell for feed-kort. Ytelse: KUN cover-bildet lastes når lista vises.
 * Resten av bildene (bruker + Google, fra images-underkolleksjonen) hentes FØRST
 * når brukeren faktisk forsøker å bla — og bare det aktive bildet rendres/lastes.
 * Bruker-bilder kommer først (getSpotImages sorterer bruker-først).
 */
const dedupe = (arr: string[]) => Array.from(new Set(arr.filter(Boolean)));

const FeedCarousel: React.FC<Props> = ({ spotId, coverUrl, extraUrls, onAllBroken }) => {
  const initial = dedupe([coverUrl, ...(extraUrls || [])]);
  const urlsRef = useRef<string[]>(initial);
  const [urls, setUrls] = useState<string[]>(initial);
  const [idx, setIdx] = useState(0);
  const [broken, setBroken] = useState<Set<string>>(new Set());
  // URL-er der BÅDE thumb og rå URL er 404 — helt døde. Ref (ikke state) fordi vi
  // kun bruker den til å avgjøre om alle bilder er borte, ikke til rendring.
  const deadRef = useRef<Set<string>>(new Set());
  const fetchedRef = useRef(false);
  const [expanded, setExpanded] = useState(initial.length > 1);

  // Hent resten av bildene én gang, on-demand (ved første bla).
  const ensureLoaded = useCallback(async (): Promise<string[]> => {
    if (fetchedRef.current || !spotId) return urlsRef.current;
    fetchedRef.current = true;
    const imgs = await getSpotImages(spotId);
    const merged = dedupe([...urlsRef.current, ...imgs]);
    urlsRef.current = merged;
    setUrls(merged);
    setExpanded(merged.length > 1);
    return merged;
  }, [spotId]);

  const nav = useCallback(async (e: React.MouseEvent, dir: 1 | -1) => {
    e.stopPropagation();
    e.preventDefault();
    const list = await ensureLoaded();
    if (list.length <= 1) return;
    setIdx((i) => (i + dir + list.length) % list.length);
  }, [ensureLoaded]);

  // Vis piler KUN når vi faktisk kjenner flere enn ett bilde. Tidligere ble de vist
  // spekulativt så snart spotId var satt (før henting), noe som ga piler på plasser
  // med bare ett bilde. Ekstra bilder fra images-underkolleksjonen (typisk Google)
  // hentes fortsatt ved første bla for fler-bilde-plasser via ensureLoaded().
  const showNav = urls.length > 1;
  const current = urls[Math.min(idx, urls.length - 1)] || coverUrl;
  // Vis thumbnail for ytelse; fall tilbake på rå URL hvis thumben 404-er (thumbs
  // genereres asynkront og finnes ikke alltid). getThumbUrl no-op-er for ikke-
  // storage-URL-er (Google-proxy o.l.), så dette er trygt for alle kilder.
  const displaySrc = broken.has(current) ? current : getThumbUrl(current);

  const handleError = () => {
    if (displaySrc !== current) {
      // Thumben 404-et → fall tilbake på rå URL.
      setBroken((prev) => new Set(prev).add(current));
      return;
    }
    // Rå URL 404-et også → dette bildet er helt dødt. Er ALLE kjente bilder døde,
    // be kortet vise satellitt i stedet for et brutt bilde.
    deadRef.current.add(current);
    if (urlsRef.current.length > 0 && urlsRef.current.every((u) => deadRef.current.has(u))) {
      onAllBroken?.();
    }
  };

  return (
    <div className="w-full h-full">
      <img
        src={displaySrc}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        alt=""
        loading="lazy"
        decoding="async"
        onError={handleError}
      />
      {showNav && (
        <>
          <button
            aria-label="Forrige bilde"
            onClick={(e) => nav(e, -1)}
            className="absolute left-1.5 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            aria-label="Neste bilde"
            onClick={(e) => nav(e, 1)}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
          >
            <ChevronRight size={16} />
          </button>
        </>
      )}
      {expanded && urls.length > 1 && (
        <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 z-20 flex gap-1">
          {urls.map((_, i) => (
            <span key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? 'bg-white' : 'bg-white/40'}`} />
          ))}
        </div>
      )}
    </div>
  );
};

export default FeedCarousel;
